// Branded PDF export for the admin Student Roster.
//
// `renderRosterPdf` holds the pure layout (drawn into a jsPDF doc) so it can be
// exercised in isolation; `downloadRosterPdf` is the browser entry point that
// lazily loads jsPDF + autotable (keeps them out of the main bundle), pulls in
// the Learnable wordmark, renders, and triggers the download.

const BRAND = {
  orange: [240, 137, 35],
  darkOrange: [214, 116, 16],
  cream: [252, 230, 204],
  rowTint: [255, 247, 237],
  gray: [111, 111, 111],
  ink: [40, 46, 56],
  border: [228, 230, 235],
  white: [255, 255, 255],
  green: [21, 128, 61],
  faint: [176, 180, 188],
};

const LOGO_SRC = '/LearnableLogo_noTag.png';

// Fetch an image and return { dataUrl, w, h }. Resolves to null on any failure
// so the caller can fall back to a text wordmark.
async function loadImage(src) {
  try {
    const res = await fetch(src);
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
    const dims = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    });
    if (!dims) return null;
    return { dataUrl, ...dims };
  } catch {
    return null;
  }
}

export function renderRosterPdf({ doc, autoTable, logo, title, sessionLabel, providerLine, ceuHours, agencies, records, generatedOn }) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;

  // ── Header: wordmark (left) + document label (right) ──
  if (logo && logo.dataUrl) {
    const logoH = 30;
    const logoW = logo.w * (logoH / logo.h);
    doc.addImage(logo.dataUrl, 'PNG', margin, margin, logoW, logoH);
  } else {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(22);
    doc.setTextColor(...BRAND.gray); doc.text('learn', margin, margin + 22);
    const w = doc.getTextWidth('learn');
    doc.setTextColor(...BRAND.orange); doc.text('able', margin + w, margin + 22);
  }
  doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(...BRAND.ink);
  doc.text('Course Completion Roster', pageW - margin, margin + 13, { align: 'right' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...BRAND.gray);
  doc.text('Learning Linq  |  Continuing Education', pageW - margin, margin + 27, { align: 'right' });

  const ruleY = margin + 44;
  doc.setDrawColor(...BRAND.orange); doc.setLineWidth(2);
  doc.line(margin, ruleY, pageW - margin, ruleY);

  // ── Course title + session ──
  let y = ruleY + 22;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...BRAND.ink);
  const titleLines = doc.splitTextToSize(title || 'Course', pageW - margin * 2);
  doc.text(titleLines, margin, y);
  y += titleLines.length * 16;
  if (sessionLabel) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(...BRAND.gray);
    doc.text(sessionLabel, margin, y);
    y += 16;
  }

  // ── Learnable provider-number + credit callout ──
  const creditText = ceuHours != null && `${ceuHours}`.trim() !== '' && isFinite(Number(ceuHours))
    ? `${Number(ceuHours)} CEU`
    : '';
  if (providerLine || creditText) {
    y += 4;
    const boxH = 28;
    doc.setFillColor(...BRAND.cream);
    doc.roundedRect(margin, y, pageW - margin * 2, boxH, 3, 3, 'F');
    if (providerLine) {
      const label = 'LEARNABLE CE PROVIDER NUMBERS';
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(...BRAND.darkOrange);
      doc.text(label, margin + 12, y + 17.5);
      const labelW = doc.getTextWidth(label);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5); doc.setTextColor(...BRAND.ink);
      doc.text(providerLine.replace(/;\s*/g, '      '), margin + 12 + labelW + 18, y + 17.5);
    }
    if (creditText) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(...BRAND.darkOrange);
      doc.text(`CREDIT AWARDED:  ${creditText}`, pageW - margin - 12, y + 17.5, { align: 'right' });
    }
    y += boxH + 16;
  }

  // ── Roster table ──
  const head = [['#', 'Name', 'Email', 'Status', 'Completed', 'Certificate #', ...agencies.map((a) => `${a}\nLicense #`)]];
  const credStart = 6;
  const body = records.map((r, i) => [
    String(i + 1),
    r.name || '',
    r.email || '',
    r.status ? r.status.charAt(0).toUpperCase() + r.status.slice(1) : '',
    r.completed || '-',
    r.certificate || '-',
    ...agencies.map((a) => r.creds[a] || '-'),
  ]);

  autoTable(doc, {
    head,
    body,
    startY: y,
    margin: { left: margin, right: margin },
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 5, textColor: BRAND.ink, lineColor: BRAND.border, lineWidth: 0.5, overflow: 'linebreak', valign: 'middle' },
    headStyles: { fillColor: BRAND.orange, textColor: BRAND.white, fontStyle: 'bold', halign: 'center', valign: 'middle', lineColor: BRAND.darkOrange, lineWidth: 0.5 },
    alternateRowStyles: { fillColor: BRAND.rowTint },
    columnStyles: {
      0: { halign: 'center', cellWidth: 26, textColor: BRAND.gray },
      1: { cellWidth: 118 },
      2: { cellWidth: 160 },
      3: { halign: 'center', cellWidth: 66 },
      4: { halign: 'center', cellWidth: 72 },
      5: { halign: 'center', cellWidth: 98 },
    },
    didParseCell: (data) => {
      if (data.column.index >= credStart) data.cell.styles.halign = 'center';
      if (data.section === 'head' && data.column.index >= credStart) data.cell.styles.fillColor = BRAND.darkOrange;
      if (data.section === 'body' && data.column.index === 3 && /completed/i.test(String(data.cell.raw))) {
        data.cell.styles.textColor = BRAND.green;
        data.cell.styles.fontStyle = 'bold';
      }
      if (data.section === 'body' && data.cell.raw === '-') data.cell.styles.textColor = BRAND.faint;
    },
    didDrawPage: () => {
      const page = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...BRAND.gray);
      doc.text(`Generated ${generatedOn}`, margin, pageH - 18);
      doc.text('Confidential - for credentialing and audit use', pageW / 2, pageH - 18, { align: 'center' });
      doc.text(`Page ${page} of {tp}`, pageW - margin, pageH - 18, { align: 'right' });
    },
  });
  if (typeof doc.putTotalPages === 'function') doc.putTotalPages('{tp}');
}

export async function downloadRosterPdf({ filename, title, sessionLabel, providerLine, ceuHours, agencies, records, generatedOn }) {
  const { jsPDF } = await import('jspdf');
  const { autoTable } = await import('jspdf-autotable');
  const logo = await loadImage(LOGO_SRC);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
  renderRosterPdf({ doc, autoTable, logo, title, sessionLabel, providerLine, ceuHours, agencies, records, generatedOn });
  doc.save(filename);
}
